/* @flow */

import { createSelector } from 'reselect';
import {
    LIMPAR_PEDIDO,
    CARREGAR_PEDIDO,
    type CarregarPedidoAction,
    type LimparPedidoAction,
} from './pedido';

import { type StateItens } from './types';
import { type GetState, type State } from '../../preact-app/types';
import { type ProdutoPedido, type ComplementoItem } from '../../produtos/types';
import { precoTotalProdutos } from '../../produtos/calculos';

export type AdicionarItemAction = {
    type: 'balcao/itens/ADICIONAR_ITEM',
    item: ProdutoPedido,
};

export type DeletarItemAction = {
    type: 'balcao/itens/DELETAR_ITEM',
    id: number,
};

export type EditarItemAction = {
    type: 'balcao/itens/EDITAR_ITEM',
    id: number,
    quantidade?: number,
    observacao?: string,
    complementos?: ComplementoItem[],
};

type Action =
    | CarregarPedidoAction
    | LimparPedidoAction
    | DeletarItemAction
    | EditarItemAction
    | AdicionarItemAction;

// Action Types

export const ADICIONAR_ITEM = 'balcao/itens/ADICIONAR_ITEM';
export const DELETAR_ITEM = 'balcao/itens/DELETAR_ITEM';
export const EDITAR_ITEM = 'balcao/itens/EDITAR_ITEM';

// Action Creators

const adicionarItemAction = (item: ProdutoPedido): AdicionarItemAction => ({
    type: ADICIONAR_ITEM,
    item,
});

export const deletarItem = (id: number): DeletarItemAction => ({
    type: DELETAR_ITEM,
    id,
});

export const editarItem = ({
    id,
    quantidade,
    observacao,
    complementos,
}: {
    id: number,
    quantidade?: number,
    observacao?: string,
    complementos?: ComplementoItem[],
}) => ({
    type: EDITAR_ITEM,
    id,
    quantidade,
    observacao,
    complementos,
});

// Selectors

export const getItens = (state: State) => state.balcao.itens;

export const getItemPorId = (state: StateItens, id: number) => state
    .find(item => item.id === id);

const getIdAutoIncremet = createSelector(
    getItens,
    state => state.map(({ id }) => id).reduce((a, b) => Math.max(a, b), 0) + 1,
);

export const getValorItens: State => number = createSelector(getItens, precoTotalProdutos);

// Thunks

export const adicionarItem = ({
    produtosId,
    quantidade = 1,
    observacao = '',
    complementos = [],
}: {
    produtosId: number,
    quantidade?: number,
    observacao?: string,
    complementos?: ComplementoItem[],
}) => (dispatch: Function, getState: GetState) => {
    const state = getState();

    const produto = state.balcao.gridProdutos.produtos.find(({ id }) => id === produtosId);
    if (!produto) {
        return;
    }

    const item = {
        id: getIdAutoIncremet(state),
        descricao: produto.descricao,
        nome: produto.nome,
        pedidosProdutosId: 0,
        preco: produto.preco,
        produtosId,
        quantidade,
        observacao,
        complementos,
    };

    dispatch(adicionarItemAction(item));
};

const itensReducer = (state: StateItens = [], action: Action): StateItens => {
    switch (action.type) {
    case ADICIONAR_ITEM: {
        return [
            action.item,
            ...state,
        ].filter(item => item.quantidade > 0);
    }
    case EDITAR_ITEM: {
        const {
            id,
            quantidade,
            observacao,
            complementos,
        } = action;

        return state
            .map(item => (item.id === id ? {
                ...item,
                quantidade: quantidade || item.quantidade,
                observacao: observacao || item.observacao,
                complementos: complementos || item.complementos,
            } : item))
            .filter(item => item.quantidade > 0);
    }
    case DELETAR_ITEM:
        return state.filter(({ quantidade, id }) => id !== action.id && quantidade > 0);
    case CARREGAR_PEDIDO:
        return action.produtos;
    case LIMPAR_PEDIDO:
        return [];
    default:
        return state;
    }
};

export default itensReducer;
